import React, { useState } from 'react';
import { Download, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useInView from '../components/useInView';
import CTABanner from '../components/CTABanner';

const GostCard = ({ gost, index, isSelected, onSelect }) => {
  const [ref, inView] = useInView();

  return (
    <div
      ref={ref}
      onClick={() => onSelect(isSelected ? null : gost.id)}
      style={{
        background: 'rgba(18,22,36,0.7)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${isSelected ? gost.color + '60' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '20px',
        padding: '28px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.96)',
        transitionProperty: 'all',
        transitionDuration: `0.6s`,
        transitionDelay: `${index * 0.1}s`,
        boxShadow: isSelected ? `0 20px 60px ${gost.color}20` : 'none',
        borderTop: `3px solid ${gost.color}`,
      }}
      onMouseOver={e => {
        if (!isSelected) {
          e.currentTarget.style.transform = 'translateY(-6px)';
          e.currentTarget.style.boxShadow = `0 20px 50px ${gost.color}18`;
        }
      }}
      onMouseOut={e => {
        if (!isSelected) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {/* GOST number badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 14px',
        borderRadius: '8px',
        background: `${gost.color}18`,
        border: `1px solid ${gost.color}40`,
        fontSize: '12px',
        fontWeight: 800,
        color: gost.color,
        marginBottom: '16px',
        letterSpacing: '0.5px',
      }}>
        {gost.id}
      </div>

      <h3 style={{
        fontSize: '15px',
        fontWeight: 700,
        color: '#fff',
        lineHeight: 1.5,
        marginBottom: '10px',
      }}>
        {gost.fullName}
      </h3>
      <p style={{
        fontSize: '13px',
        color: 'rgba(255,255,255,0.55)',
        lineHeight: 1.7,
        marginBottom: '20px',
      }}>
        {gost.scope}
      </p>

      {/* Key points - collapsible */}
      <div style={{
        maxHeight: isSelected ? '400px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <div style={{
          paddingTop: '16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          marginBottom: '16px',
        }}>
          <div style={{
            fontSize: '11px',
            color: gost.color,
            fontWeight: 700,
            letterSpacing: '2px',
            marginBottom: '12px',
          }}>
            КЛЮЧЕВЫЕ ПУНКТЫ
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {gost.keyPoints.map((point, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: '10px',
                  padding: '10px 12px',
                  background: `${gost.color}08`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.7)',
                  animation: isSelected ? `fadeInUp 0.3s ease ${i * 0.05}s both` : 'none',
                }}
              >
                <span style={{ color: gost.color, fontWeight: 700, fontFamily: 'monospace', flexShrink: 0 }}>
                  {point.ref}
                </span>
                <span>{point.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          color: gost.color,
          fontWeight: 600,
        }}>
          {isSelected ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {isSelected ? 'Свернуть' : 'Ключевые пункты'}
        </div>
        <button
          onClick={e => { e.stopPropagation(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            background: `${gost.color}15`,
            border: `1px solid ${gost.color}30`,
            borderRadius: '8px',
            color: gost.color,
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={e => e.currentTarget.style.background = `${gost.color}30`}
          onMouseOut={e => e.currentTarget.style.background = `${gost.color}15`}
        >
          <Download size={13} />
          Скачать
        </button>
      </div>
    </div>
  );
};

const GostsPage = () => {
  const { t } = useLang();
  const [selectedGost, setSelectedGost] = useState(null);
  const [headerRef, headerInView] = useInView();
  const [schemeRef, schemeInView] = useInView();

  const gosts = [
    {
      id: 'ГОСТ Р 54869-2011',
      fullName: t('gost1_full'),
      scope: t('gost1_scope'),
      color: '#2E5AFF',
      keyPoints: [
        { ref: '§ 5.2', text: 'Инициация проекта — разработка Паспорта/Устава' },
        { ref: '§ 5.3', text: 'Планирование — 7 подпроцессов' },
        { ref: '§ 5.4', text: 'Организация исполнения проекта' },
        { ref: '§ 5.5', text: 'Контроль и мониторинг исполнения' },
        { ref: '§ 5.6', text: 'Завершение проекта — архивирование, анализ опыта' },
      ],
    },
    {
      id: 'ГОСТ Р 54870-2011',
      fullName: t('gost2_full'),
      scope: t('gost2_scope'),
      color: '#8A2BE2',
      keyPoints: [
        { ref: '§ 4.2', text: 'Формирование портфеля проектов организации' },
        { ref: '§ 4.3', text: 'Оптимизация и балансировка портфеля' },
        { ref: '§ 4.4', text: 'Мониторинг и управление портфелем' },
        { ref: '§ 4.5', text: 'Стратегическое согласование проектов' },
      ],
    },
    {
      id: 'ГОСТ Р 54871-2011',
      fullName: t('gost3_full'),
      scope: t('gost3_scope'),
      color: '#C050FF',
      keyPoints: [
        { ref: '§ 4.2', text: 'Инициация программы' },
        { ref: '§ 4.3', text: 'Планирование программы' },
        { ref: '§ 4.4', text: 'Управление преимуществами программы' },
        { ref: '§ 4.5', text: 'Закрытие программы' },
      ],
    },
    {
      id: 'ИСО 21500:2012',
      fullName: t('gost4_full'),
      scope: t('gost4_scope'),
      color: '#FF3B9A',
      keyPoints: [
        { ref: '§ 3', text: 'Концепции управления проектами' },
        { ref: '§ 4', text: '39 процессов управления проектами' },
        { ref: 'Гарм.', text: 'Гармонизирован с ГОСТ Р 54869-2011' },
        { ref: 'ISO', text: 'Международное признание сертификатов' },
      ],
    },
  ];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px' }}>
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '60px 24px 48px',
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 0.8s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '2px', background: 'linear-gradient(90deg, #2E5AFF, #8A2BE2)' }} />
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#2E5AFF', letterSpacing: '3px', textTransform: 'uppercase' }}>
            Standards
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', alignItems: 'end' }}>
          <h1 style={{
            fontSize: 'clamp(28px, 4vw, 48px)',
            fontWeight: 900,
            letterSpacing: '-1.5px',
            lineHeight: 1.1,
            color: '#fff',
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #2E5AFF, #8A2BE2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>ГОСТы</span>{' '}и стандарты
          </h1>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
            {t('gosts_sub')}
          </p>
        </div>
      </div>

      {/* GOST cards grid */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 24px 80px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
      }}>
        {gosts.map((gost, i) => (
          <GostCard
            key={gost.id}
            gost={gost}
            index={i}
            isSelected={selectedGost === gost.id}
            onSelect={setSelectedGost}
          />
        ))}
      </div>

      {/* Relationship scheme */}
      <div
        ref={schemeRef}
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 24px 80px',
          opacity: schemeInView ? 1 : 0,
          transform: schemeInView ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 0.8s ease',
        }}
      >
        <div style={{
          background: 'rgba(18,22,36,0.7)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '24px',
          padding: '40px',
        }}>
          <h2 style={{
            fontSize: 'clamp(20px, 3vw, 30px)',
            fontWeight: 800,
            color: '#fff',
            marginBottom: '8px',
            letterSpacing: '-0.5px',
          }}>
            {t('gosts_scheme_title')}
          </h2>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.45)', marginBottom: '40px' }}>
            {t('gosts_scheme_sub')}
          </p>

          {/* Hierarchy diagram */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}>
            {/* Portfolio level */}
            <div style={{
              padding: '16px 40px',
              background: 'linear-gradient(135deg, rgba(138,43,226,0.15), rgba(192,80,255,0.1))',
              border: '1px solid rgba(138,43,226,0.4)',
              borderRadius: '12px',
              textAlign: 'center',
              marginBottom: '0',
              width: '100%',
              maxWidth: '320px',
            }}>
              <div style={{ fontSize: '10px', color: '#8A2BE2', fontWeight: 700, letterSpacing: '2px', marginBottom: '4px' }}>ПОРТФЕЛЬ ПРОЕКТОВ</div>
              <div style={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>ГОСТ Р 54870-2011</div>
            </div>

            {/* Connector */}
            <div style={{ width: '2px', height: '30px', background: 'rgba(138,43,226,0.4)' }} />

            {/* Program level */}
            <div style={{ display: 'flex', gap: '16px', width: '100%', justifyContent: 'center' }}>
              <div style={{
                padding: '14px 28px',
                background: 'linear-gradient(135deg, rgba(192,80,255,0.12), rgba(138,43,226,0.08))',
                border: '1px solid rgba(192,80,255,0.35)',
                borderRadius: '12px',
                textAlign: 'center',
                flex: '0 1 220px',
              }}>
                <div style={{ fontSize: '10px', color: '#C050FF', fontWeight: 700, letterSpacing: '2px', marginBottom: '4px' }}>ПРОГРАММА</div>
                <div style={{ fontSize: '12px', color: '#fff', fontWeight: 600 }}>ГОСТ Р 54871-2011</div>
              </div>
              <div style={{
                padding: '14px 28px',
                background: 'linear-gradient(135deg, rgba(192,80,255,0.12), rgba(138,43,226,0.08))',
                border: '1px solid rgba(192,80,255,0.35)',
                borderRadius: '12px',
                textAlign: 'center',
                flex: '0 1 220px',
              }}>
                <div style={{ fontSize: '10px', color: '#C050FF', fontWeight: 700, letterSpacing: '2px', marginBottom: '4px' }}>ПРОГРАММА</div>
                <div style={{ fontSize: '12px', color: '#fff', fontWeight: 600 }}>ГОСТ Р 54871-2011</div>
              </div>
            </div>

            {/* Connectors */}
            <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', maxWidth: '500px' }}>
              {[0, 1].map(i => (
                <div key={i} style={{ width: '2px', height: '30px', background: 'rgba(46,90,255,0.4)' }} />
              ))}
            </div>

            {/* Projects level */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
              {[1, 2, 3, 4].map(n => (
                <div
                  key={n}
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, rgba(46,90,255,0.12), rgba(46,90,255,0.06))',
                    border: '1px solid rgba(46,90,255,0.35)',
                    borderRadius: '10px',
                    textAlign: 'center',
                    flex: '0 1 130px',
                  }}
                >
                  <div style={{ fontSize: '9px', color: '#2E5AFF', fontWeight: 700, letterSpacing: '1.5px', marginBottom: '3px' }}>ПРОЕКТ</div>
                  <div style={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>ГОСТ Р 54869</div>
                  <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>ИСО 21500</div>
                </div>
              ))}
            </div>
          </div>

          {/* ISO note */}
          <div style={{
            marginTop: '32px',
            padding: '16px 20px',
            background: 'rgba(255,59,154,0.06)',
            border: '1px solid rgba(255,59,154,0.2)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <ExternalLink size={16} style={{ color: '#FF3B9A', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)' }}>
              <strong style={{ color: '#FF3B9A' }}>ИСО 21500:2012</strong> — международный аналог ГОСТ Р 54869-2011, обеспечивает глобальное признание компетенций
            </span>
          </div>
        </div>
      </div>

      <CTABanner />
    </div>
  );
};

export default GostsPage;
